import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { pickerPage } from "../../lib/paging";
import { Link, useNavigate, useParams } from "react-router";
import { Button, Card, Cluster, Drawer, EmptyState, Input, ProgressBar, Stack } from "@salesapp/ui";
import { opsApi, type CycleRun } from "../../lib/opsApi";
import { crmApi, type Company } from "../../lib/crmApi";
import {
  ACTION_HINTS,
  CYCLE_RUN_STATUS_OPTIONS,
  CYCLE_STAGE_STATUS_OPTIONS,
  FIELD_HINTS,
  optionMeaning,
} from "../../help/fieldHints";
import { ListWorkspace, DeleteRecordButton } from "./RecordWorkspace";
import { ResponsiveActions } from "./ResponsiveActions";
import { StatusBadge } from "./StatusBadge";
import { useAccess } from "../../shell/AccessProvider";
import { useAuth } from "../../shell/AuthProvider";
import { useLocale } from "../../shell/LocaleProvider";
import { SalesCycleSettings } from "./SalesCycleSettings";
import { useCrmHref } from "../../lib/customerUrl";
import { SalesCycleDiagram } from "./SalesCycleDiagram";
import {
  annotateCycleStages,
  dueCardStyle,
  dueUrgency,
  dueUrgencyLabel,
  matchesDueFilter,
  type DueFilter,
} from "./salesCycleDue";
import { estimateLabel, isEstimateUnit } from "./salesCycleEstimate";

function companyLabel(company: Company) {
  return [company.company_code, company.legal_name].filter(Boolean).join(" ");
}

function windowLabel(formatDate: (value: string) => string, startsAt?: string | null, endsAt?: string | null) {
  const from = startsAt ? formatDate(startsAt) : "—";
  const to = endsAt ? formatDate(endsAt) : "—";
  return `from ${from} to ${to}`;
}

function flowTone(status: string | null | undefined): "started" | "not-started" | "done" {
  if (status === "done") return "done";
  if (status === "started") return "started";
  return "not-started";
}

function CompanyCombobox({
  companies,
  value,
  onChange,
}: {
  companies: Company[];
  value: string;
  onChange: (companyId: string) => void;
}) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const selected = companies.find((company) => company.id === value);
  const selectedLabel = selected ? companyLabel(selected) : "";
  const term = query.trim().toLowerCase();
  const browsing = !term || term === selectedLabel.toLowerCase();
  const filtered = useMemo(() => {
    if (browsing) return companies;
    return companies.filter((company) => companyLabel(company).toLowerCase().includes(term));
  }, [browsing, companies, term]);

  useEffect(() => {
    if (!open) setQuery(selected ? companyLabel(selected) : "");
  }, [open, selected]);

  useEffect(() => {
    setActiveIndex(0);
  }, [term]);

  useEffect(() => {
    if (!open) return;
    function handlePointer(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handlePointer);
    return () => document.removeEventListener("mousedown", handlePointer);
  }, [open]);

  function choose(company: Company) {
    onChange(company.id);
    setQuery(companyLabel(company));
    setOpen(false);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.min(current + (open ? 1 : 0), Math.max(filtered.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === "Enter") {
      const match = filtered[activeIndex];
      if (open && match) {
        event.preventDefault();
        choose(match);
      }
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="crm-company-combobox" ref={rootRef}>
      <Input
        role="combobox"
        aria-label="Choose a company"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && filtered[activeIndex] ? `${listId}-${filtered[activeIndex].id}` : undefined}
        autoComplete="off"
        placeholder="Choose a company"
        title={ACTION_HINTS.choose_cycle_company}
        value={open ? query : selected ? companyLabel(selected) : query}
        onChange={(event) => {
          const next = event.target.value;
          setQuery(next);
          setOpen(true);
          const match = companies.find((company) => companyLabel(company).toLowerCase() === next.trim().toLowerCase());
          onChange(match?.id ?? "");
        }}
        onFocus={(event) => {
          setQuery(selected ? companyLabel(selected) : query);
          setOpen(true);
          event.currentTarget.select();
        }}
        onKeyDown={onKeyDown}
      />
      {open ? (
        <ul className="crm-company-combobox__list" id={listId} role="listbox">
          {filtered.length === 0 ? (
            <li className="crm-muted">No matching companies</li>
          ) : (
            filtered.map((company, index) => (
              <li key={company.id} role="presentation">
                <button
                  type="button"
                  id={`${listId}-${company.id}`}
                  role="option"
                  aria-selected={company.id === value}
                  className={index === activeIndex ? "crm-company-combobox__option is-active" : "crm-company-combobox__option"}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(company)}
                >
                  {companyLabel(company)}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

export function SalesCyclePage({ orgId }: { orgId: string }) {
  const { can } = useAccess();
  const { session } = useAuth();
  const navigate = useNavigate();
  const crmHref = useCrmHref();
  const { formatDate } = useLocale();
  const [runs, setRuns] = useState<CycleRun[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [runData, companyData] = await Promise.all([
        opsApi.listCycleRuns(orgId),
        crmApi.listCompanies(orgId, undefined, pickerPage()),
      ]);
      setRuns(runData.runs);
      setCompanies(companyData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sales cycles could not be loaded.");
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function start() {
    if (!companyId) return;
    setError(null);
    try {
      const run = await opsApi.startCycle(orgId, companyId);
      navigate(crmHref(`/sales-cycle/${run.id}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sales cycle could not be started.");
    }
  }

  return (
    <ListWorkspace
      title="Sales cycle"
      toolbar={<p className="home-subtitle">Each customer run keeps the seven-stage playbook. Open a customer to see the cycle map and click a stage to work it.</p>}
      actions={
        <ResponsiveActions
          className="crm-cycle-toolbar"
          primary={
            can("crm.sales_cycle", "create") ? (
              <>
                <CompanyCombobox companies={companies} value={companyId} onChange={setCompanyId} />
                <Button
                  className="crm-cycle-start"
                  title={ACTION_HINTS.start_sales_cycle}
                  onClick={() => start()}
                  disabled={!companyId}
                >
                  Start sales cycle
                </Button>
              </>
            ) : null
          }
          more={
            can("crm.sales_cycle", "configure") ? (
              <Button type="button" variant="secondary" title={ACTION_HINTS.cycle_settings} onClick={() => setSettingsOpen(true)}>
                Settings
              </Button>
            ) : null
          }
        />
      }
    >
      {error && <p className="crm-error">{error}</p>}
      {runs.length === 0 ? (
        <EmptyState title="No active sales cycles" description="Start a cycle from a company to give the salesperson the staged checklist." />
      ) : (
        <table className="crm-table">
          <thead>
            <tr>
              <th title={FIELD_HINTS.opportunity_company}>Company</th>
              <th title={FIELD_HINTS.sales_cycle_run_status}>Status</th>
              <th title={FIELD_HINTS.sales_cycle_started}>Started</th>
              <th className="crm-table-actions-col" title="Row actions for this sales cycle">Actions</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr key={run.id}>
                <td>
                  <Link to={crmHref(`/sales-cycle/${run.id}`)} title="Open the cycle map and stage checklist for this company.">
                    {run.company_code} {run.company_name}
                  </Link>
                </td>
                <td>
                  <StatusBadge status={run.status} options={CYCLE_RUN_STATUS_OPTIONS} />
                </td>
                <td>{run.started_at ? formatDate(run.started_at) : ""}</td>
                <td className="crm-table-actions">
                  <DeleteRecordButton
                    capability="crm.sales_cycle"
                    recordLabel={`${run.company_code} ${run.company_name}`.trim()}
                    size="sm"
                    variant="ghost"
                    onDeleted={async () => {
                      await opsApi.deleteCycleRun(orgId, run.id);
                      await load();
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Drawer wide isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} title="Sales cycle settings">
        <SalesCycleSettings
          orgId={orgId}
          canEditDefault={Boolean(session?.isOrgAdmin)}
          territoriesOn={can("crm.territories")}
        />
      </Drawer>
    </ListWorkspace>
  );
}

export function SalesCycleRunPage({ orgId }: { orgId: string }) {
  const { can } = useAccess();
  const { session } = useAuth();
  const { runId = "" } = useParams<{ runId: string }>();
  const crmHref = useCrmHref();
  const { formatDate, timezone } = useLocale();
  const navigate = useNavigate();
  const [run, setRun] = useState<Awaited<ReturnType<typeof opsApi.getCycleRun>> | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [comment, setComment] = useState<Record<string, string>>({});
  const [dueFilter, setDueFilter] = useState<DueFilter>("all");
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRun(await opsApi.getCycleRun(orgId, runId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sales cycle could not be loaded.");
    }
  }, [orgId, runId]);

  useEffect(() => {
    void load();
  }, [load]);

  const stages = useMemo(
    () => (run ? annotateCycleStages(run.stages, run.started_at, new Date(), timezone) : []),
    [run, timezone],
  );
  const openStage = stages.find((stage) => stage.id === selectedStageId) ?? null;
  const taskProgress = useMemo(() => {
    const tasks = stages.flatMap((stage) => stage.tasks);
    if (tasks.length === 0) return 0;
    return Math.round((tasks.filter((task) => task.status === "done").length / tasks.length) * 100);
  }, [stages]);

  async function move(taskId: string, status: "todo" | "doing" | "done") {
    setError(null);
    // Clear the box up front so a repeat click cannot resend the same comment; restore it on failure.
    const draft = comment[taskId];
    setComment((current) => ({ ...current, [taskId]: "" }));
    try {
      setRun(await opsApi.updateCycleTask(orgId, runId, taskId, { status, comment: draft }));
    } catch (err) {
      setComment((current) => ({ ...current, [taskId]: current[taskId] || draft || "" }));
      setError(err instanceof Error ? err.message : "Task could not be updated.");
    }
  }

  async function saveStageStatus(stageId: string, status: "started" | "not_started") {
    setError(null);
    try {
      setRun(await opsApi.updateCycleStage(orgId, runId, stageId, status));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Stage could not be updated.");
    }
  }

  function toggleStage(stageId: string) {
    setSelectedStageId((current) => (current === stageId ? null : stageId));
  }

  if (!run) return <Card>{error ?? "Loading sales cycle…"}</Card>;

  return (
    <Stack gap={4}>
      <div>
        <Link to={crmHref("/sales-cycle")}>Back to sales cycles</Link>
        <Cluster gap={3} align="start" justify="between">
          <h1 className="home-title">{run.company_code} {run.company_name}</h1>
          <Cluster gap={2} align="center">
            {can("crm.sales_cycle", "configure") ? (
              <Button type="button" variant="secondary" title={ACTION_HINTS.cycle_settings} onClick={() => setSettingsOpen(true)}>
                Settings
              </Button>
            ) : null}
            <DeleteRecordButton
              capability="crm.sales_cycle"
              recordLabel={`${run.company_code} ${run.company_name}`.trim()}
              onDeleted={async () => {
                await opsApi.deleteCycleRun(orgId, runId);
                navigate(crmHref("/sales-cycle"));
              }}
            />
          </Cluster>
        </Cluster>
        <p className="home-subtitle">The map is the cycle. The left column is the timed flow — start a stage yourself or wait for its dates. Click a stage name in the flow, or its slice on the map, to work that process. Stages stay green while on track, turn amber when something is due soon, and red when overdue.</p>
      </div>
      {error && <p className="crm-error">{error}</p>}
      <div className={openStage ? "crm-cycle-run is-open" : "crm-cycle-run"}>
        <aside className="crm-cycle-flow" aria-label="Sales cycle flow">
          <div title={ACTION_HINTS.cycle_progress}>
            <ProgressBar value={taskProgress} tone="success" label="Cycle progress" />
          </div>
          <ol className="crm-cycle-steps">
            {stages.map((stage) => {
              const tone = flowTone(stage.flow_status);
              const selectValue = tone === "not-started" ? "not_started" : "started";
              return (
                <li key={stage.id} className={`crm-cycle-step is-${tone}${openStage?.id === stage.id ? " is-selected" : ""}`}>
                  <button
                    type="button"
                    className="crm-cycle-step-title"
                    aria-pressed={openStage?.id === stage.id}
                    aria-label={`Show tasks for ${stage.name}`}
                    title={ACTION_HINTS.flow_stage}
                    onClick={() => toggleStage(stage.id)}
                  >
                    {stage.index + 1}. {stage.name}
                  </button>
                  <p className="crm-muted">{windowLabel(formatDate, stage.starts_at, stage.ends_at)}</p>
                  <select
                    className="crm-select"
                    value={selectValue}
                    disabled={tone === "done"}
                    aria-label={`${stage.name} status`}
                    title={FIELD_HINTS.sales_cycle_stage_status}
                    onChange={(event) => void saveStageStatus(stage.id, event.target.value as "started" | "not_started")}
                  >
                    <option value="started" title={optionMeaning(CYCLE_STAGE_STATUS_OPTIONS, "started")}>
                      Started
                    </option>
                    <option value="not_started" title={optionMeaning(CYCLE_STAGE_STATUS_OPTIONS, "not_started")}>
                      Not started
                    </option>
                  </select>
                </li>
              );
            })}
          </ol>
        </aside>
        <div className="crm-cycle-map">
          <SalesCycleDiagram stages={stages} selectedId={openStage?.id ?? null} onSelect={toggleStage} />
          <ul className="crm-cycle-legend">
            <li className="is-green" title="On track — more than two days until due.">
              On track
            </li>
            <li className="is-amber" title="Due soon — within the next two days.">
              Due soon
            </li>
            <li className="is-red" title="Overdue — due today or already past.">
              Overdue
            </li>
          </ul>
        </div>
        {openStage ? (
          <section className="crm-cycle-stage-panel" aria-label={openStage.name}>
            <Cluster gap={3} align="center" justify="between">
              <h2>{openStage.name}</h2>
              <Cluster gap={2} align="center">
                <select
                  className="crm-select"
                  value={dueFilter}
                  onChange={(event) => setDueFilter(event.target.value as DueFilter)}
                  aria-label="Filter tasks by due date"
                  title={ACTION_HINTS.filter_task_due}
                >
                  <option value="all" title="Show every task in this stage.">
                    All
                  </option>
                  <option value="green" title="On track — more than two days until due.">
                    Green
                  </option>
                  <option value="amber" title="Due soon — within the next two days.">
                    Amber
                  </option>
                  <option value="red" title="Overdue — due today or already past.">
                    Red
                  </option>
                </select>
                <Button variant="ghost" type="button" title={ACTION_HINTS.close_stage_panel} onClick={() => setSelectedStageId(null)}>
                  Close stage
                </Button>
              </Cluster>
            </Cluster>
            {!openStage.active ? (
              <p className="crm-muted">This stage opens as the customer ages into it, or when the previous stage is done.</p>
            ) : null}
            <div className="crm-cycle-board">
              {(["todo", "doing", "done"] as const).map((bucket) => (
                <div key={bucket} className={`crm-cycle-bucket is-${bucket}`}>
                  <h3
                    title={
                      bucket === "todo"
                        ? "Tasks not started yet."
                        : bucket === "doing"
                          ? "Tasks currently in progress."
                          : "Tasks marked complete."
                    }
                  >
                    {bucket === "todo" ? "To do" : bucket === "doing" ? "Doing" : "Done"}
                  </h3>
                  {openStage.tasks.filter((task) => task.status === bucket && matchesDueFilter(task.due_at, dueFilter, new Date(), timezone)).map((task) => {
                    const urgency = task.due_at ? dueUrgency(task.due_at, new Date(), timezone) : null;
                    return (
                      <Card
                        key={task.id}
                        padding="sm"
                        className={urgency ? `crm-cycle-task is-due-${urgency}` : "crm-cycle-task"}
                        style={urgency ? dueCardStyle(urgency) : undefined}
                        data-due-urgency={urgency ?? undefined}
                      >
                        <strong>{task.title}</strong>
                        {task.estimate_amount && task.estimate_unit && isEstimateUnit(task.estimate_unit) ? (
                          <p className="crm-muted">Estimate {estimateLabel(task.estimate_amount, task.estimate_unit)}</p>
                        ) : null}
                        {task.due_at && (
                          <p className="crm-muted">
                            Due {formatDate(task.due_at)}
                            {urgency ? (
                              <span
                                className={`crm-cycle-due-label is-${urgency}`}
                                title={
                                  urgency === "green"
                                    ? "On track — more than two days until due."
                                    : urgency === "amber"
                                      ? "Due soon — within the next two days."
                                      : "Overdue — due today or already past."
                                }
                              >
                                {dueUrgencyLabel(urgency)}
                              </span>
                            ) : null}
                          </p>
                        )}
                        {task.comments.map((item) => (
                          <p key={item.id} className="crm-muted">{item.body}</p>
                        ))}
                        <Input
                          value={comment[task.id] ?? ""}
                          onChange={(event) => setComment((current) => ({ ...current, [task.id]: event.target.value }))}
                          placeholder="Comment"
                          title={ACTION_HINTS.task_comment}
                          aria-label={`Comment for ${task.title}`}
                        />
                        <div className="crm-cycle-actions">
                          {bucket !== "todo" && (
                            <Button variant="secondary" title={ACTION_HINTS.task_todo} onClick={() => move(task.id, "todo")}>
                              To do
                            </Button>
                          )}
                          {bucket !== "doing" && (
                            <Button variant="secondary" title={ACTION_HINTS.task_doing} onClick={() => move(task.id, "doing")}>
                              Doing
                            </Button>
                          )}
                          {bucket !== "done" && (
                            <Button title={ACTION_HINTS.task_done} onClick={() => move(task.id, "done")}>
                              Done
                            </Button>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
      <Drawer
        wide
        isOpen={settingsOpen}
        onClose={() => {
          setSettingsOpen(false);
          void load();
        }}
        title="Sales cycle settings"
      >
        <SalesCycleSettings
          orgId={orgId}
          canEditDefault={Boolean(session?.isOrgAdmin)}
          territoriesOn={can("crm.territories")}
        />
      </Drawer>
    </Stack>
  );
}
